import React, { useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import Modal from "components/Modal";
import { CheckBox, Loading, MultiSelect } from "components/form";
import { axiosInstance, handleErrors, handleSuccessMessages } from "store/auth";
import { useCore } from "store/core";

const PRODUCT_TYPES = ["Movie", "Serie", "Music", "Screening", "SerieEpisode"];

const normalizeOptions = (list = []) => {
  return (list || [])
    .map((item) => {
      if (
        item &&
        typeof item === "object" &&
        "value" in item &&
        "label" in item
      ) {
        return item;
      }

      if (item && typeof item === "object" && "id" in item) {
        return {  
          value: item.id,
          label:
            item.title ||
            item.name ||
            item.fa_name ||
            item.display_name ||
            item.user_name ||
            `#${item.id}`,
        };
      }

      return null;
    })
    .filter(Boolean);
};

const toIdList = (list = []) =>
  (list || [])
    .map((item) => item?.id)
    .filter((id) => Number.isInteger(id) || /^\d+$/.test(String(id)))
    .map((id) => Number(id));

const buildEffectiveUrl = (userId, page, filters) => {
  let url = `/panel/catalog-users/${userId}/effective-products?page=${page}&perpage=10`;

  if (filters.search) {
    url += `&search=${encodeURIComponent(filters.search)}`;
  }

  if (filters.type) {
    url += `&type=${encodeURIComponent(filters.type)}`;
  }

  if (filters.category_id) {
    url += `&category_id=${filters.category_id}`;
  }

  return url;
};

export default function ManageAccessModal({ show, user, onClose, onSaved }) {
  const core = useCore();
  const [loading, setLoading] = useState(false);
  const [accessData, setAccessData] = useState(null);

  const [effectiveLoading, setEffectiveLoading] = useState(false);
  const [effectiveProducts, setEffectiveProducts] = useState([]);
  const [effectiveMeta, setEffectiveMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  const [effectiveFilters, setEffectiveFilters] = useState({
    search: "",
    type: "",
    category_id: "",
  });

  const loadEffectiveProducts = async (page = 1, customFilters = null) => {
    if (!user?.id) {
      return;
    }

    const filters = customFilters || effectiveFilters;

    setEffectiveLoading(true);
    try {
      const result = await axiosInstance.get(
        buildEffectiveUrl(user.id, page, filters),
      );
      const payload = result.data || {};

      setEffectiveProducts(payload.data || []);
      setEffectiveMeta(
        payload.meta || { current_page: 1, last_page: 1, total: 0 },
      );
    } catch (e) {
      handleErrors(e);
    } finally {
      setEffectiveLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!show || !user?.id) {
        return;
      }

      setLoading(true);
      try {
        await Promise.all([
          core.dispatch(core.getProducts()),
          core.dispatch(core.getProductCategories()),
        ]);

        const accessResult = await axiosInstance.get(
          `/panel/catalog-users/${user.id}/access`,
        );
        setAccessData(accessResult.data);

        await loadEffectiveProducts(1, {
          search: "",
          type: "",
          category_id: "",
        });
      } catch (e) {
        handleErrors(e);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
    // Core actions are store-provided and this request lifecycle is keyed by modal/user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user?.id]);

  const handleRemoveProduct = async (productId) => {
    try {
      await axiosInstance.put(`/panel/catalog-users/${user.id}/access`, {
        mode: "remove",
        product_ids: [productId],
        category_ids: [],
      });

      handleSuccessMessages("محصول از دسترسی کاربر حذف شد");

      const updatedAccess = await axiosInstance.get(
        `/panel/catalog-users/${user.id}/access`,
      );
      setAccessData(updatedAccess.data);

      await loadEffectiveProducts(effectiveMeta.current_page || 1);
    } catch (e) {
      handleErrors(e);
    }
  };

  const productOptions = useMemo(() => {
    const direct = normalizeOptions(
      (accessData?.direct_products || []).map((item) => ({
        value: item.id,
        label: `${item.title || "-"} (${item.type || "-"}) - #${item.id}`,
      })),
    );

    const all = normalizeOptions(core.products || []);
    const merged = [...direct, ...all];
    const byId = new Map();

    merged.forEach((option) => {
      if (!byId.has(option.value)) {
        byId.set(option.value, option);
      }
    });

    return Array.from(byId.values());
  }, [core.products, accessData]);

  const categoryOptions = useMemo(() => {
    const direct = normalizeOptions(
      (accessData?.categories || []).map((item) => ({
        value: item.id,
        label: `${item.name || "-"} (${item.type || "-"}) - #${item.id}`,
      })),
    );

    const all = normalizeOptions(core.productCategories || []);
    const merged = [...direct, ...all];
    const byId = new Map();

    merged.forEach((option) => {
      if (!byId.has(option.value)) {
        byId.set(option.value, option);
      }
    });

    return Array.from(byId.values());
  }, [core.productCategories, accessData]);

  const initialValues = useMemo(
    () => ({
      mode: "sync",
      product_ids: toIdList(accessData?.direct_products),
      category_ids: toIdList(accessData?.categories),
      all_plus_products: !!accessData?.all_plus_products,
    }),
    [accessData],
  );

  const directProductIds = useMemo(
    () => new Set(toIdList(accessData?.direct_products)),
    [accessData],
  );

  const resetAndClose = () => {
    setAccessData(null);
    setEffectiveProducts([]);
    setEffectiveMeta({ current_page: 1, last_page: 1, total: 0 });
    setEffectiveFilters({ search: "", type: "", category_id: "" });
    onClose();
  };

  return (
    <Modal
      show={show}
      handleClose={resetAndClose}
      title={`مدیریت دسترسی کاتالوگ${
        user?.full_name ? ` - ${user.full_name}` : ""
      }`}
      size="modal-xl"
    >
      {loading && (
        <div className="modal-body">
          <Loading />
        </div>
      )}

      {!loading && accessData && (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload = {
                mode: values.mode,
                product_ids: values.product_ids || [],
                category_ids: values.category_ids || [],
                all_plus_products: !!values.all_plus_products,
              };

              const result = await axiosInstance.put(
                `/panel/catalog-users/${user.id}/access`,
                payload,
              );

              setAccessData(result.data);
              await loadEffectiveProducts(1);

              handleSuccessMessages("دسترسی کاربر با موفقیت ذخیره شد");
              if (onSaved) {
                onSaved();
              }
            } catch (e) {
              handleErrors(e);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="modal-body gap-y-3">
                <div className="intro-x grid md:grid-cols-3 md:gap-3">
                  <div className="text-xs mt-2">
                    <label
                      className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="mode"
                    >
                      حالت اعمال دسترسی:
                    </label>
                    <Field
                      as="select"
                      name="mode"
                      className="border-0 px-3 py-3 form-control placeholder-blueGray-300 text-blueGray-600 bg-violet-50 rounded text-xs shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    >
                      <option value="sync">sync - جایگزینی کامل</option>
                      <option value="add">add - افزودن</option>
                      <option value="remove">remove - حذف</option>
                    </Field>
                  </div>

                  <div className="text-xs mt-2 flex items-end">
                    <div className="text-sm text-gray-700">
                      محصولات مستقیم:{" "}
                      <b>{accessData?.direct_products?.length || 0}</b>
                    </div>
                  </div>

                  <div className="text-xs mt-2 flex items-end">
                    <div className="text-sm text-gray-700">
                      محصولات موثر:{" "}
                      <b>{accessData?.effective_products_count || 0}</b>
                    </div>
                  </div>
                </div>

                <div className="intro-x grid md:grid-cols-2 md:gap-3">
                  <Field
                    label="محصولات مستقیم"
                    name="product_ids"
                    valueOnly
                    component={MultiSelect}
                    options={productOptions}
                  />

                  <Field
                    label="دسته بندی ها"
                    name="category_ids"
                    valueOnly
                    component={MultiSelect}
                    options={categoryOptions}
                  />
                </div>

                <div className="intro-x rounded bg-violet-50 px-3 pb-3">
                  <Field
                    label="دسترسی به همه محصولات پلاس"
                    name="all_plus_products"
                    component={CheckBox}
                  />
                  <div className="text-xs text-gray-600 mr-7">
                    با فعال بودن این گزینه، محصولات پلاس فعلی و محصولاتی که در
                    آینده پلاس می‌شوند به‌صورت خودکار در دسترس کاربر خواهند بود.
                  </div>
                </div>

                <div className="p-3 rounded bg-violet-50 text-xs text-gray-700">
                  با `add` موارد انتخابی اضافه می‌شود، با `remove` موارد انتخابی
                  حذف می‌شود و با `sync` فقط همین مقادیر باقی می‌ماند.
                </div>

                <div className="mt-4 border-t pt-4">
                  <h4 className="font-medium mb-3">محصولات موثر کاربر</h4>

                  <div className="intro-x grid md:grid-cols-4 md:gap-3">
                    <div className="text-xs mt-2">
                      <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">
                        جستجو
                      </label>
                      <input
                        aria-label="جستجوی محصولات موثر"
                        className="border-0 px-3 py-3 form-control placeholder-blueGray-300 text-blueGray-600 bg-violet-50 rounded text-xs shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                        value={effectiveFilters.search}
                        onChange={({ target: { value } }) =>
                          setEffectiveFilters((prev) => ({
                            ...prev,
                            search: value,
                          }))
                        }
                        placeholder="عنوان محصول"
                      />
                    </div>

                    <div className="text-xs mt-2">
                      <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">
                        نوع محصول
                      </label>
                      <select
                        aria-label="نوع محصول موثر"
                        className="border-0 px-3 py-3 form-control placeholder-blueGray-300 text-blueGray-600 bg-violet-50 rounded text-xs shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                        value={effectiveFilters.type}
                        onChange={({ target: { value } }) =>
                          setEffectiveFilters((prev) => ({
                            ...prev,
                            type: value,
                          }))
                        }
                      >
                        <option value="">همه</option>
                        {PRODUCT_TYPES.map((item) => (
                          <option value={item} key={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="text-xs mt-2">
                      <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">
                        دسته بندی
                      </label>
                      <select
                        aria-label="دسته‌بندی محصول موثر"
                        className="border-0 px-3 py-3 form-control placeholder-blueGray-300 text-blueGray-600 bg-violet-50 rounded text-xs shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                        value={effectiveFilters.category_id}
                        onChange={({ target: { value } }) =>
                          setEffectiveFilters((prev) => ({
                            ...prev,
                            category_id: value,
                          }))
                        }
                      >
                        <option value="">همه</option>
                        {categoryOptions.map((item) => (
                          <option
                            value={item.value}
                            key={`cat-filter-${item.value}`}
                          >
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="text-xs mt-2 flex items-end gap-2">
                      <button
                        type="button"
                        className="btn btn-primary w-24"
                        onClick={() => loadEffectiveProducts(1)}
                        disabled={effectiveLoading}
                      >
                        اعمال
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-24"
                        onClick={() => {
                          const resetFilters = {
                            search: "",
                            type: "",
                            category_id: "",
                          };
                          setEffectiveFilters(resetFilters);
                          loadEffectiveProducts(1, resetFilters);
                        }}
                        disabled={effectiveLoading}
                      >
                        پاکسازی
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 overflow-auto">
                    <table className="table table-report -mt-2">
                      <thead>
                        <tr>
                          <th className="whitespace-nowrap text-center">
                            شناسه
                          </th>
                          <th className="whitespace-nowrap text-center">
                            عنوان
                          </th>
                          <th className="whitespace-nowrap text-center">نوع</th>
                          <th className="whitespace-nowrap text-center">
                            قیمت
                          </th>
                          <th className="whitespace-nowrap text-center">
                            تاریخ ایجاد
                          </th>
                          <th className="whitespace-nowrap text-center">
                            نوع دسترسی / عملیات
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {effectiveLoading && (
                          <tr>
                            <td colSpan={6} className="text-center p-4">
                              <Loading />
                            </td>
                          </tr>
                        )}

                        {!effectiveLoading && effectiveProducts.length < 1 && (
                          <tr>
                            <td colSpan={6} className="text-center p-4">
                              محصولی یافت نشد.
                            </td>
                          </tr>
                        )}

                        {!effectiveLoading &&
                          effectiveProducts.map((item) => (
                            <tr
                              className="intro-x"
                              key={`effective-product-${item.id}`}
                            >
                              <td className="text-center">{item.id}</td>
                              <td className="text-center">
                                {item.title || "-"}
                              </td>
                              <td className="text-center">
                                {item.type || "-"}
                              </td>
                              <td className="text-center">
                                {item?.price?.amount || 0}
                              </td>
                              <td className="text-center">
                                {item.created_at || "-"}
                              </td>
                              <td className="text-center">
                                {directProductIds.has(Number(item.id)) ? (
                                  <button
                                    type="button"
                                    aria-label={`remove-direct-product-${item.id}`}
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleRemoveProduct(item.id)}
                                  >
                                    ❌ حذف دسترسی مستقیم
                                  </button>
                                ) : (
                                  <span className="text-theme-9">
                                    از طریق دسته‌بندی یا پلاس
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-gray-700">
                      تعداد کل محصولات موثر: {effectiveMeta.total || 0}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-20"
                        disabled={
                          effectiveLoading ||
                          (effectiveMeta.current_page || 1) <= 1
                        }
                        onClick={() =>
                          loadEffectiveProducts(
                            (effectiveMeta.current_page || 1) - 1,
                          )
                        }
                      >
                        قبلی
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-20"
                        disabled={
                          effectiveLoading ||
                          (effectiveMeta.current_page || 1) >=
                            (effectiveMeta.last_page || 1)
                        }
                        onClick={() =>
                          loadEffectiveProducts(
                            (effectiveMeta.current_page || 1) + 1,
                          )
                        }
                      >
                        بعدی
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer text-right">
                <button
                  className="btn btn-primary w-24"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loading /> : "ذخیره"}
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={resetAndClose}
                  className="btn btn-outline-secondary w-24 mr-1"
                >
                  بستن
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
}
